import React, { useState, useEffect, useRef } from "react";
import styled from "styled-components";
import DismissibleInfoBanner from "../../Components/Generic/DismissibleInfoBanner";
import { lightThemeColors } from "../../assets/styles/colors";
import PrimaryButton from "../../Components/Generic/PrimaryButton";
import SecondaryButton from "../../Components/Generic/SecondaryButton";
import { ReactComponent as CopyIcon } from "../../assets/icons/copyIcon.svg";
import { ReactComponent as BinIcon } from "../../assets/icons/bin-emotii.svg";
import { useKeysContext } from "../../context/useKeysContext";
import { copyToClipboard } from "../../helper/copy";
import { useProjectContext } from "../../context/useProjectContext";
import { useOutletContext } from "react-router-dom";
import { showGenericError, showErrorNotification } from "../../helper/Notifications";
import CustomModal from "../../Components/Generic/CustomModal";
import Tooltip from "../../Components/Generic/Tooltip";
import DocumentationPageModal from "../../Components/ApiKeyPage/DocumentationPageModal";
import ApiKeyFormModal from "../../Components/ApiKeyPage/ApiKeyFormModal";
import { AnimatePresence } from "framer-motion";
import { executeBulkDelete } from "../../helper/bulkOperations";
import BulkActionBar from "../../Components/Generic/BulkActionBar";
import StyledTable, {
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "../../Components/Generic/StyledTable";
import TablePagination from "../../Components/Generic/TablePagination";
import { useLicensingContext } from "../../context/useLicensingContext";
import LoaderComponent from "../../Components/Generic/LoaderComponent";
import SubscriptionCards from "../SubscriptionsPage/SubscriptionCards";
import Dropdown from "../../Components/Generic/Dropdown";
import KeyStatusFilter from "../../Components/Generic/KeyStatusFilter";
import Toggle from "../../Components/Generic/Toggle";
import { CURRENCY_OPTIONS } from "../../constants/OptionsConstants"
import usePurchaseSubscription from "../../hooks/usePurchaseSubscription";
import AppDateRangePicker from "../../Components/Generic/AppDateRangePicker";
import PageHeader from "../../Components/Layout/PageHeader";
import useMemberProductAccess from "../../hooks/useMemberProductAccess";
import MemberAccessBanner from "../../Components/Generic/MemberAccessBanner";
import ProductUsageWidget from "../../Components/Generic/ProductUsageWidget";
import { formatDate, formatDateRangeForAPI, getDefaultMonthToDateRange, isDateRangeSelected } from "../../helper/dateFormat";
import useSortedPagination from "../../hooks/useSortedPagination";

const isNotEmbeddedKey = (key) => key && key.key_type !== 'embedded';

const ApiKeysPage = () => {
  const [showKeyModal, setShowKeyModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [showDocumentationModal, setShowDocumentationModal] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [selectedKeys, setSelectedKeys] = useState([]);
  const [showBulkDeleteModal, setShowBulkDeleteModal] = useState(false);

  const [keyToEdit, setKeyToEdit] = useState(null);

  const [itemsPerPage, setItemsPerPage] = useState(20);

  const { keys, deleteKey, getKeys, createKey, updateKey} = useKeysContext();
  const { selectedProject } = useProjectContext();
  const { subscription } = useLicensingContext();
  const { setLoading, setShowSupportModal } = useOutletContext();
  const [currency, setCurrency] = useState(() => subscription?.currency || "GBP");
  const {
    handlePurchase,
    annualPricing,
    setAnnualPricing,
    effectiveAnnual,
    billingInterval,
  } = usePurchaseSubscription();
  const [dateRange, setDateRange] = useState(getDefaultMonthToDateRange);
  const [keyStatus, setKeyStatus] = useState("active");

  const hasDateRange = isDateRangeSelected(dateRange);
  const {
    page, setPage, totalPages, setTotalPages,
    sortBy, ascending, toggleSort, sortArrow, goToPrevious, goToNext,
  } = useSortedPagination({
    initialSortBy: "words",
    initialAscending: false,
    isColumnDisabled: (col) => col === "words" && !hasDateRange,
  });
  const { hasProductAccess: hasMemberApiAccess } = useMemberProductAccess("api");
  const hasApiAccess = subscription?.items?.some(i => i.product_slug === "api") || hasMemberApiAccess;
  const hasExistingKeys = keys && keys.length > 0;
  
  const mapKey = (key) => {
    if (!key) return "-";
    if (key.length > 20) {
      return key.slice(0, 6) + "***************" + key.slice(-6);
    } else return key;
  };

  const handleGetKeys = () => {
    setLoading(true);

    const extraParams = {
     sort_by: (sortBy === "words" && !hasDateRange) ? "created_at" : sortBy,
     ascending: ascending,
     ...(hasDateRange && (() => {
     const dateParams = formatDateRangeForAPI(dateRange[0], dateRange[1]);
      return { start_date: dateParams.start_date, end_date: dateParams.end_date };
     })())
    };

    getKeys(
      selectedProject.id,   
      page,                
      itemsPerPage,        
      (response) => {      
        setTotalPages(response.data.pagination?.total_pages || 1);
        setLoading(false);
      },
      () => {              
        showGenericError();
        setLoading(false);
      },
      extraParams,
      "api",
      keyStatus
    );
  };

  const handleOpenDeleteModal = (key) => {
    setKeyToDelete(key);
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setKeyToDelete(null);
  };

  const handleDeleteKey = () => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      deleteKey(
        selectedProject.id,
        keyToDelete.id,
        (response) => {
          handleGetKeys();
          resolve();
        },
        (error) => {
          showGenericError();
          setLoading(false);
          reject(error);
        }
      );
    });
  };

  const handleOpenAddModal = () => {
    setKeyToEdit(null);
    setShowKeyModal(true);
  };

  const handleOpenEditModal = (key) => {
    setKeyToEdit(key);
    setShowKeyModal(true);
  };

  const handleCloseKeyModal = () => {
    setShowKeyModal(false);
    setKeyToEdit(null);
  };

  const handleCreateApiKeys = async (drafts) => {
    setLoading(true);

    try {
      for (const draft of drafts) {
        await new Promise((resolve, reject) => {
          createKey(
            draft.name,
            draft.details,
            selectedProject.id,
            () => resolve(),
            (error) => reject(error),
            "api"
          );
        });
      }

      handleGetKeys();
      handleCloseKeyModal();
      return Promise.resolve();
    } catch (error) {
      showGenericError();
      setLoading(false);
      return Promise.reject(error);
    }
  };

  const handleUpdateApiKey = (draft) => {
    return new Promise((resolve, reject) => {
      setLoading(true);
      updateKey(
        selectedProject.id,
        keyToEdit.id,
        draft.name,
        () => {
          handleGetKeys();
          handleCloseKeyModal();
          resolve();
        },
        (error) => {
          showGenericError();
          setLoading(false);
          reject(error);
        },
        draft.details
      );
    });
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const allIds = displayKeys
        .filter(isNotEmbeddedKey)
        .map((key) => key.id);
      setSelectedKeys(allIds);
    } else {
      setSelectedKeys([]);
    }
  };

const handleSelectOne = (e, keyId) => {
  if (e.target.checked) {
    setSelectedKeys((prev) => [...prev, keyId]);
  } else {
    setSelectedKeys((prev) => prev.filter((id) => id !== keyId));
  }
};

  useEffect(() => {
    setPage(1);
  }, [selectedProject?.id]);

  useEffect(() => {
    setPage(1);
    setSelectedKeys([]);
  }, [keyStatus]);

  useEffect(() => {
    if (!selectedProject || subscription === undefined) return;
    if (!hasApiAccess && !hasExistingKeys) return;
    handleGetKeys();
  }, [selectedProject, page, itemsPerPage, dateRange, sortBy, ascending, subscription, hasApiAccess, keyStatus]);

  const handleBulkDeleteKeys = () => {
    const deleteSingleKey = (keyId) => {
      return new Promise((resolve, reject) => {
        deleteKey(selectedProject.id, keyId, resolve, reject);
      });
    };

    return executeBulkDelete({
      items: selectedKeys,
      deleteAction: deleteSingleKey,
      onSuccess: () => {
        handleGetKeys();
        setSelectedKeys([]);
      },
      setLoading: setLoading
    });
  };

  useEffect(() => {
    if (subscription?.currency) {
      setCurrency(subscription.currency);
    }
  }, [subscription]);

  if (subscription === undefined) {
    return <LoaderComponent loading={true} />;
  }

  const displayKeys = keys;
  const hasSelection = selectedKeys.length > 0;
  const isInactiveView = keyStatus === "inactive";

  return (
    <Container>
      <AnimatePresence>
        {showDocumentationModal && (
          <DocumentationPageModal
            setShowDocumentationModal={setShowDocumentationModal}
            subscriptionDetails={subscription}
            productType="api"
          />
        )}
      </AnimatePresence>

      {/* Delete API Key Modal */}
      <CustomModal
        isOpen={showDeleteModal}
        onOpenChange={handleCloseDeleteModal}
        iconType="warning"
        iconAlignment="center"
        title="Delete this API key?"
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
          {
            text: "Cancel",
            onClick: handleCloseDeleteModal,
            variant: "secondary"
          },
          {
            text: "Delete Key",
            action: handleDeleteKey,
            variant: "delete",
            successMessage: "API key deleted successfully"
          }
        ]}
      >
        <ModalContentText>
          This will immediately revoke the key and disable any integrations or automations using it. You cannot recover this key once deleted - you'll need to generate a new one if needed.
        </ModalContentText>
      </CustomModal>

      {/* Add API Key Modal */}
      <ApiKeyFormModal
        isOpen={showKeyModal}
        onClose={handleCloseKeyModal}
        productTitle="API for Text"
        keyLabel="API for Text key"
        subtitle="Generate a key to start building integrations or automating tasks. Keep it safe - it works like a password for your account."
        namePlaceholder="e.g. Backend Integration"
        keyToEdit={keyToEdit}
        onCreate={handleCreateApiKeys}
        onUpdate={handleUpdateApiKey}
      />
      <CustomModal
        isOpen={showBulkDeleteModal}
        onOpenChange={() => setShowBulkDeleteModal(false)}
        iconType="warning"
        iconAlignment="center"
        title={`Delete ${selectedKeys.length} API key${selectedKeys.length === 1 ? '' : 's'}?`}
        titleAlignment="center"
        buttonLayout="row"
        showCloseButton={false}
        autoCloseOnSuccess={true}
        width={400}
        buttons={[
        {
          text: "Cancel",
          onClick: () => setShowBulkDeleteModal(false),
          variant: "secondary"
        },
        {
        text: `Delete ${selectedKeys.length} Key${selectedKeys.length === 1 ? '' : 's'}`,
        action: handleBulkDeleteKeys,
        variant: "delete",
        successMessage: `API key${selectedKeys.length === 1 ? '' : 's'} deleted successfully`
        }
    ]}
  >
  <ModalContentText>
    This will immediately revoke <strong>{selectedKeys.length}</strong> key{selectedKeys.length === 1 ? '' : 's'} and disable any integrations or automations using {selectedKeys.length === 1 ? 'it' : 'them'}. You cannot recover {selectedKeys.length === 1 ? 'this key' : 'these keys'} once deleted.
  </ModalContentText>
</CustomModal>

    <PageContent>
        <Header>
          <HeaderContent>
            <PageHeader
              title={(hasApiAccess || hasExistingKeys) ? "API for Text" : "API for Text Plans"}
              subtitle={(hasApiAccess || hasExistingKeys)
                ? "Enable API for Text access to integrate our platform with your tools."
                : "Choose a plan to unlock API for Text access and start building integrations."}
            />
          </HeaderContent>
          <HeaderRight>
            {!(hasApiAccess || hasExistingKeys) && (
              <Dropdown
                value={currency}
                onValueChange={setCurrency}
                size="default"
                options={CURRENCY_OPTIONS}
                disabled={!!billingInterval}
              />
            )}
            {(hasApiAccess || hasExistingKeys) && (
              <HeaderActions>
              <ButtonsContainer>
                <SecondaryButton
                  icon={<CodeBracketsIcon />}
                  text={"See Documentation"}
                  onClick={() => setShowDocumentationModal(true)}
                  variant="brandTint"
                />
                <PrimaryButton
                  icon={<AddApiKeyIcon />}
                  text={"Add API Key"}
                  onClick={handleOpenAddModal}
                />
              </ButtonsContainer>
              </HeaderActions>
          )}
          </HeaderRight>
        </Header>
        {(hasApiAccess || hasExistingKeys) && (
          <BannersGroup>
            <MemberAccessBanner />
            <DismissibleInfoBanner
              cookieKey="apiKeysBannerDismissed"
              text="API keys are your secure way to connect apps and scripts to our platform. Generate a key, and start building custom integrations or automating tasks. You can manage and revoke keys whenever needed."
            />
          </BannersGroup>
        )}

        {(hasApiAccess || hasExistingKeys) && selectedProject && (
          <ProductUsageWidget
            projectId={selectedProject.id}
            productSlug="api"
            dateRange={dateRange}
            calendarAction={
              <AppDateRangePicker
                value={dateRange}
                onChange={(newRange) => setDateRange(newRange)}
              />
            }
          />
        )}

        {!(hasApiAccess || hasExistingKeys) ? (
          <PlanCardsSection>
            <PlanCardsHeader>
              <PricingLabel>Pricing plans</PricingLabel>
              {!billingInterval && (
                <label className="inline-flex items-center gap-3 cursor-pointer select-none">
                  <Toggle
                    checked={annualPricing}
                    onCheckedChange={setAnnualPricing}
                  />
                  <span className="font-inter text-[16px] leading-6 font-medium text-brand-900">
                    Annual pricing <span className="text-brand-700 font-medium">(save 10%)</span>
                  </span>
                </label>
              )}
            </PlanCardsHeader>

            <SubscriptionCards
              purchaseSubscription={handlePurchase}
              contactSales={() => setShowSupportModal(true)}
              annual={effectiveAnnual}
              currency={currency}
              product="api"
            />
          </PlanCardsSection>
        ) : (
          <>
          <TableControlsRow>
            <KeyStatusFilter value={keyStatus} onValueChange={setKeyStatus} />
          </TableControlsRow>
          <StyledTable>
            <TableHeader>

              <BulkActionBar
                selectedCount={selectedKeys.length}
                onDelete={() => setShowBulkDeleteModal(true)} 
                onClear={() => setSelectedKeys([])} 
              />
              

              <TableRow>
                <TableHead style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                  {!isInactiveView && (
                    <CheckboxContainer $alwaysVisible={hasSelection}>
                      <input
                        type="checkbox"
                        style={{ cursor: "pointer", margin: 0 }}
                        checked={
                          displayKeys?.length > 0 &&
                          selectedKeys.length === displayKeys.filter(isNotEmbeddedKey).length
                        }
                        onChange={handleSelectAll}
                      />
                    </CheckboxContainer>
                  )}
                </TableHead>
                <TableHead style={{ textAlign: "left", width: "18%" }}>Name</TableHead>
                <TableHead style={{ textAlign: "center" }}>
                   {!hasDateRange ? (
                      <Tooltip content="Select a date range to sort by words" side="top">
                        <span>
                          <HeaderButton onClick={() => toggleSort("words")} disabled={!hasDateRange}>
                            Words{sortArrow("words")}
                          </HeaderButton>
                        </span>
                      </Tooltip>
                     ) : (
                      <HeaderButton onClick={() => toggleSort("words")}>
                         Words{sortArrow("words")}
                      </HeaderButton>
                    )}
                </TableHead>
                <TableHead style={{ textAlign: "center" }}>Minutes</TableHead>
                <TableHead style={{ textAlign: "center", width: "150px" }}>
                  {isInactiveView ? (
                    "Deleted On"
                  ) : (
                    <HeaderButton onClick={() => toggleSort("created_at")}>
                      Created On{sortArrow("created_at")}
                    </HeaderButton>
                  )}
                </TableHead>
                <TableHead style={{ textAlign: "center", width: "250px" }}>Details</TableHead>
                <TableHead style={{ textAlign: "center" }}>Value</TableHead>
                <TableHead style={{ width: "150px", textAlign: "center" }}>Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {displayKeys && displayKeys.length > 0 ? (
                displayKeys.filter(isNotEmbeddedKey).map((key) => (
                  <TableRow key={key.id}>
                    <TableCell style={{ width: "48px", verticalAlign: "middle", padding: "0 8px" }}>
                      {!isInactiveView && (
                        <CheckboxContainer $alwaysVisible={hasSelection || selectedKeys.includes(key.id)}>
                          <input
                            type="checkbox"
                            style={{ cursor: "pointer", margin: 0 }}
                            checked={selectedKeys.includes(key.id)}
                            onChange={(e) => handleSelectOne(e, key.id)}
                          />
                        </CheckboxContainer>
                      )}
                    </TableCell>
           
                    <TableCell>
                      <KeyNameContainer>
                        <KeyIconWrapper>
                          <KeyNameIcon />
                        </KeyIconWrapper>
                        <KeyName>{key.name}</KeyName>
                      </KeyNameContainer>
                    </TableCell>
  
                  <TableCell style={{ textAlign: "center" }}>
                    {key.words ? key.words.toLocaleString() : "0"}
                  </TableCell>

                  <TableCell style={{ textAlign: "center" }}>
                    {((key.audio_minutes || 0) + (key.video_minutes || 0)).toFixed(1)}
                  </TableCell>

                  <TableCell style={{ textAlign: "center" }}>
                      <DateText>{formatDate(key.created_at)}</DateText>
                    </TableCell>

                    <TableCell style={{ textAlign: "center" }}>
                      {key.details ? (
                        <Tooltip content={key.details} side="top">
                          <ReasonText>{key.details}</ReasonText>
                         </Tooltip>
                      ) : (
                        <span style={{ color: "#9ca3af" }}>-</span>
                       )}
                    </TableCell>

                    <TableCell style={{ textAlign: "center" }}>
                      <KeyValue data-emotii-exclude>{mapKey(key.key)}</KeyValue>
                    </TableCell>
                    <TableCell style={{ width: "150px" }}>
                      {isInactiveView ? (
                        <span style={{ color: "#9ca3af" }}>-</span>
                      ) : (
                        <ActionButtonsContainer>
                          <Tooltip content="Copy the API Key" side="top">
                            <CopyButton onClick={() => copyToClipboard(key.key)}>
                              <CopyIcon />
                            </CopyButton>
                          </Tooltip>
                          <Tooltip content="Edit API Key" side="top">
                            <EditButton onClick={() => handleOpenEditModal(key)}>
                              <PenIcon />
                            </EditButton>
                          </Tooltip>
                          <DeleteButton onClick={() => handleOpenDeleteModal(key)}>
                            <BinIcon />
                          </DeleteButton>
                        </ActionButtonsContainer>
                      )}
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={8}>
                    <NoKeysText>
                      API keys are your secure way to connect apps and scripts to our platform. Generate a key, and start building custom integrations or automating tasks. You can manage and revoke keys whenever needed.
                    </NoKeysText>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
            <TablePagination
              page={page}
              totalPages={totalPages}
              onPrevious={goToPrevious}
              onNext={goToNext}
              leftColSpan={2}
              centerColSpan={4}
              rightColSpan={2}
              itemsPerPage={itemsPerPage}
              onItemsPerPageChange={(v) => { setItemsPerPage(v); setPage(1); }}
            />
          </StyledTable>
          </>
        )}
      </PageContent>
    </Container>
  );
};

export default ApiKeysPage;

const AddApiKeyIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M1.66675 14.167L10.0001 18.3337L18.3334 14.167M1.66675 10.0003L10.0001 14.167L18.3334 10.0003M10.0001 1.66699L1.66675 5.83366L10.0001 10.0003L18.3334 5.83366L10.0001 1.66699Z" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const KeyNameIcon = (props) => (
  <svg width="40" height="40" viewBox="0 0 40 40" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <rect width="40" height="40" rx="20" fill="#FFEAD5"/>
    <path d="M10 25L20 30L30 25M10 20L20 25L30 20M20 10L10 15L20 20L30 15L20 10Z" stroke="#FD8539" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CodeBracketsIcon = (props) => (
  <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M6.66667 14.1667L2.5 10L6.66667 5.83334M13.3333 5.83334L17.5 10L13.3333 14.1667" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const PenIcon = (props) => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" {...props}>
    <path d="M11.3333 2.00004C11.5084 1.82494 11.7163 1.68605 11.9451 1.59129C12.1739 1.49653 12.419 1.44775 12.6666 1.44775C12.9143 1.44775 13.1594 1.49653 13.3882 1.59129C13.617 1.68605 13.8249 1.82494 14 2.00004C14.1751 2.17513 14.314 2.383 14.4087 2.61182C14.5035 2.84063 14.5523 3.08569 14.5523 3.33337C14.5523 3.58106 14.5035 3.82612 14.4087 4.05493C14.314 4.28375 14.1751 4.49162 14 4.66671L5.00001 13.6667L1.33334 14.6667L2.33334 11L11.3333 2.00004Z" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const PageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  overflow-y: auto;
  padding: 30px 30px 18px 30px;
  position: relative;
  height: 100%;
  background: #F9FAFB;
`;

const BannersGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  background: white;
  margin: -40px -30px 0 -30px;
  padding: 30px 30px 10px 30px;
`;

const HeaderRight = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-shrink: 0;
`;

const HeaderContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  flex: 1;
`;

const ButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const HeaderTitle = styled.h1`
  color: ${lightThemeColors.darkColor};
  font-size: 32px;
  font-weight: 700;
  line-height: 40px;
  text-align: left;
  margin: 0;
`;

const HeaderSubtitle = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`;

const PlanCardsSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  width: 100%;
`;

const PlanCardsHeader = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 16px;
  position: relative;
`;

const PricingLabel = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  height: 28px;
  padding: 2px 12px;
  border-radius: 16px;
  background: #F4EBFF;
  color: #6941C6;
  font-size: 14px;
  font-weight: 500;
  line-height: 20px;
`;

const ModalContentText = styled.p`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  margin: 0;
`;

const KeyNameContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const KeyIconWrapper = styled.div`
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;

  svg {
    width: 40px;
    height: 40px;
    display: block;
  }
`;

const KeyName = styled.span`
  color: #111827; 
  font-size: 14px; 
  font-weight: 600; 
  line-height: 20px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  max-width: 200px;
  display: block;
`;

const KeyValue = styled.span`
  color: ${lightThemeColors.darkColor};
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  font-family: 'Courier New', monospace;
`;

const ActionButtonsContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: 8px;
`;

const CopyButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f4ebff;
    color: #7f56d9;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const DeleteButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #fef2f2;
    color: #ef4444;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const EditButton = styled.button`
  background: none;
  border: none;
  cursor: pointer;
  padding: 8px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: #9ca3af;
  transition: all 0.2s;

  &:hover {
    background: #f4ebff;
    color: #7f56d9;
  }

  svg {
    width: 16px;
    height: 16px;
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }

  svg path {
    fill: none !important;
    stroke: currentColor !important;
    stroke-width: 1.5 !important;
  }
`;

const NoKeysText = styled.div`
  color: #667085;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  text-align: center;
  width: 100%;
  padding: 40px 20px;
`;

const CheckboxContainer = styled.div`
  opacity: ${(props) => (props.$alwaysVisible ? 1 : 0)};
  transition: opacity 0.15s ease-in-out;
  display: flex;
  align-items: center;
  justify-content: center;

  tr:hover & {
    opacity: 1;
  }
`;

const DateText = styled.span`
  color: #111827;
  font-size: 14px;
  font-weight: 400;
  line-height: 20px;
  white-space: nowrap; 
`;

const ReasonText = styled.span`
  color: #667085; 
  font-size: 13px;
  font-weight: 400;
  line-height: 20px;

  display: inline-block;
  max-width: 200px;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
  vertical-align: middle;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 16px;
`;

const TableControlsRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: flex-end;
`;

const HeaderButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  background: transparent;
  border: none;
  cursor: pointer;
  color: #374151;
  font-weight: 600;
  padding: 0;
  font-size: inherit;

  &:hover {
    color: #111827;
  }
`;
